import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { gameService } from '../../services/games';

const initialState = {
  level: null,
  levelRef: uuidv4(),
  completed: false,
  attempts: 0,
  loading: false,
  error: null,
};

/**
 * Async thunk for fetching the next level for a specific game
 * @param {string} gameName - The name of the game (e.g., 'learndle-word')
 */
export const fetchNextLevel = createAsyncThunk(
  'level/fetchNextLevel',
  async (gameName, { getState, rejectWithValue }) => {
    try {
      // Get userId and minSchoolYear from the Redux state
      const state = getState();
      const userId = state.childProfile.childProfile?.id;
      const minSchoolYear =
        state.childProfile.childProfile?.min_school_year || 1;

      if (!userId) {
        throw new Error('User ID is required to fetch next level');
      }

      if (!gameName) {
        throw new Error('Game name is required to fetch next level');
      }

      // Map game names to their corresponding service methods
      let gameServiceModule;
      switch (gameName) {
        case 'learndle-word':
          gameServiceModule = gameService.learndleWord;
          break;
        // Add more game types here as they are implemented
        // case 'crossword':
        //   gameServiceModule = gameService.crossword;
        //   break;
        default:
          throw new Error(`Unknown game type: ${gameName}`);
      }

      // Fetch the next level from the service
      const level = await gameServiceModule.getNextLevel(userId, minSchoolYear);

      return level;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Async thunk for checking an answer for the current level
 * @param {Object} payload - Object containing guess and gameName
 * @param {string} payload.guess - The user's guess
 * @param {string} payload.gameName - The name of the game (e.g., 'learndle-word')
 */
export const checkLevelAnswer = createAsyncThunk(
  'level/checkAnswer',
  async ({ guess, gameName }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const currentLevel = state.level.level;

      if (!currentLevel) {
        throw new Error('No level loaded to check answer against');
      }

      if (!guess) {
        throw new Error('Guess is required to check answer');
      }

      // Map game names to their corresponding service methods
      let gameServiceModule;
      switch (gameName) {
        case 'learndle-word':
          gameServiceModule = gameService.learndleWord;
          break;
        // Add more game types here as they are implemented
        default:
          throw new Error(`Unknown game type: ${gameName}`);
      }

      // Check the answer using the service
      const result = await gameServiceModule.checkAnswer(
        guess,
        currentLevel.word
      );

      return result;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const levelSlice = createSlice({
  name: 'level',
  initialState,
  reducers: {
    setLevel: (state, action) => {
      state.level = action.payload;
      state.error = null;
    },
    updateLevelRef: (state) => {
      state.levelRef = uuidv4();
    },
    updateStatus: (state, action) => {
      state.completed = action.payload.completed;
      state.attempts = action.payload.attempts;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNextLevel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNextLevel.fulfilled, (state, action) => {
        state.loading = false;
        state.level = action.payload;
        state.error = null;
      })
      .addCase(fetchNextLevel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch next level';
      })
      .addCase(checkLevelAnswer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkLevelAnswer.fulfilled, (state, action) => {
        state.loading = false;
        const { validAttempt, isCorrect } = action.payload;

        // Increment attempts if it was a valid attempt
        if (validAttempt) {
          state.attempts += 1;
        }

        // Mark as completed if the answer is correct
        if (isCorrect) {
          state.completed = true;
        }

        state.error = null;
      })
      .addCase(checkLevelAnswer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to check answer';
      });
  },
});

export const { setLevel, updateLevelRef, updateStatus, clearError } =
  levelSlice.actions;

// Selectors
export const selectLevel = (state) => state.level.level;
export const selectLevelLoading = (state) => state.level.loading;
export const selectLevelError = (state) => state.level.error;
export const selectLevelRef = (state) => state.level.levelRef;
export const selectLevelCompleted = (state) => state.level.completed;
export const selectLevelAttempts = (state) => state.level.attempts;

export default levelSlice.reducer;
